import React, { useState, useEffect, useMemo, useRef, RefObject } from "react";
import clsx from "clsx";

import type { ServerDataSuccess } from "./route";
import { Sheet } from "~/components/Sheet";
import { Sections } from "~/components/Sections/Sections";

import { useRichText } from "~/hooks/useRichText";

import {
  type TableProps,
  SanityImage,
  ShopifyRichText,
  Table,
  TabbedSectionProps,
  TabbedSection,
  IconArchSupport,
  IconCuboidNotch,
  IconHeelCup,
  IconMetatarsalDome,
  IconVintageArrow,
} from "@bared/ui";

export interface SizeAndFitProps {
  activeTab: "sizeGuide" | "biomechanicalFootbed";
  biomechanicalFootbed: ServerDataSuccess["biomechanicalFootbedSheetContents"];
  gender?: "Mens" | "Womens";
  handleSheetToggle: () => void;
  isOpen: boolean;
  sheetFooterContent: ServerDataSuccess["template"]["sheetFooterContent"];
  sizeGuide: ServerDataSuccess["sizeGuideSheetContents"];
}

export function SizeAndFitSheet(props: SizeAndFitProps) {
  const [sheetTitle, setSheetTitle] = useState<string | undefined>(undefined);

  const handleTitleChange = (tabHandle: string) => {
    const title = (() => {
      if ("sizeGuide" === tabHandle) return "Size Guide";
      if ("biomechanicalFootbed" === tabHandle) return "Biomechanical Footbed";

      return undefined;
    })();

    setSheetTitle(title);
  };

  return (
    <Sheet
      isOpen={props.isOpen}
      onClose={props.handleSheetToggle}
      title={sheetTitle}
      className="px-midi md:px-maxi"
    >
      <TabbedSheet props={props} onChange={handleTitleChange} />
    </Sheet>
  );
}

function TabbedSheet({
  props,
  onChange,
}: {
  props: SizeAndFitProps;
  onChange: TabbedSectionProps["onChange"];
}) {
  const [activeTab, setActiveTab] = useState<string>(props.activeTab);
  const { parseContent } = useRichText();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const tabs: TabbedSectionProps["tabs"] = [
    { title: "Size Guide", handle: "sizeGuide" },
    { title: `Biomechanical Footbed`, handle: "biomechanicalFootbed" },
  ];

  const footerContent = parseContent(props.sheetFooterContent);

  // Adds a click event listener to all links in the sheet container to determine if the sheet should be closed or not
  useEffect(() => {
    if (!wrapperRef.current) return;

    const links = wrapperRef.current.querySelectorAll("a");

    if (!links) return;

    links.forEach((link) => {
      if (link.target === "_blank") return;

      link.addEventListener("click", props.handleSheetToggle);
    });

    return () => {
      links.forEach((link) => {
        link.removeEventListener("click", props.handleSheetToggle);
      });
    };
  }, [wrapperRef, props.handleSheetToggle]);

  /** Adds a click event listener to all elements in the sheet container
   * that have a data-tab-to-navigate attribute to navigate between tabs
   */
  useEffect(() => {
    if (!wrapperRef.current) return;

    // Get all elements that have a data-tab-to-navigate attribute
    const elements = wrapperRef.current.querySelectorAll(
      "[data-tab-to-navigate]",
    );

    if (!elements || elements.length === 0) return;

    const navigateToTab = (e: Event) => {
      e.preventDefault();
      const currentElement = e.currentTarget as HTMLElement;
      const tabHandle = currentElement?.getAttribute("data-tab-to-navigate");

      if (tabHandle && onChange) {
        onChange(tabHandle);
        setActiveTab(tabHandle);
      }
    };

    // Add a click event listener to all elements that have a data-tab-to-navigate attribute
    elements.forEach((link) => {
      link.addEventListener("click", navigateToTab);
    });

    return () => {
      elements.forEach((link) => {
        link.removeEventListener("click", navigateToTab);
      });
    };
  }, [wrapperRef, onChange, setActiveTab]);

  return (
    <>
      <TabbedSection
        tabs={tabs}
        stickyTabTitle
        enableTabTitleScroll
        onChange={(tabHandle) => {
          setActiveTab(tabHandle);
          if (onChange) onChange(tabHandle);
        }}
        activeTab={activeTab}
      >
        {(tabHandle) => {
          if ("sizeGuide" === tabHandle)
            return (
              <SizeGuideSheetContent
                wrapperRef={wrapperRef}
                sizeGuide={props.sizeGuide}
                gender={props.gender}
              />
            );

          if ("biomechanicalFootbed" === tabHandle)
            return (
              <BiomechanicalFootbedSheetContent
                wrapperRef={wrapperRef}
                biomechanicalFootbed={props.biomechanicalFootbed}
                gender={props.gender}
              />
            );
        }}
      </TabbedSection>

      {!!footerContent && (
        <div className="border-t border-primary-300 py-midi">
          {footerContent}
        </div>
      )}
    </>
  );
}

function SizeGuideSheetContent({
  sizeGuide,
  gender,
  wrapperRef,
}: Pick<SizeAndFitProps, "sizeGuide" | "gender"> & {
  wrapperRef: RefObject<HTMLDivElement>;
}) {
  const { parseContent } = useRichText();

  if (!sizeGuide) return null;

  const tableRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const {
    men,
    women,
    summaryMens,
    summaryWomens,
    flexibleSections,
    sizingAdvice,
  } = sizeGuide;

  const sheetContent = useMemo(() => {
    if (gender === "Mens") {
      return {
        summary: summaryMens,
        sizes: men,
        flexibleSections,
        sizingAdvice,
      };
    }

    if (gender === "Womens") {
      return {
        summary: summaryWomens,
        sizes: women,
        flexibleSections,
        sizingAdvice,
      };
    }
  }, [gender]);
  const summary = parseContent(sheetContent?.summary);

  const tableHeaders: TableProps["columns"] = [
    { name: "Bared/EU", id: "bared", isRowHeader: true },
    { name: "US", id: "us_au" },
    { name: "CM", id: "cm" },
    { name: "IN", id: "inch" },
  ];

  const tableCells: TableProps["row"] = sheetContent?.sizes
    ? sheetContent.sizes.map((size, index) => ({
        id: index + 1,
        bared: size.bared,
        us_au: size.us_au,
        cm: size.cm,
        inch: size.inch,
      }))
    : [];

  // Function to update the scroll state of table (whether it's possible to scroll left or right)
  const updateTableScrollState = () => {
    if (tableRef.current) {
      const scrollLeft = Math.round(tableRef.current.scrollLeft);
      const scrollWidth = tableRef.current.scrollWidth;
      const clientWidth = tableRef.current.clientWidth;

      // Can scroll left if scrollLeft > 0
      setCanScrollLeft(scrollLeft > 0);

      // Can scroll right if the total scrollable width exceeds the visible width
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };

  const handleScrollRight = () => {
    if (tableRef.current && canScrollRight) {
      tableRef.current.scrollTo({
        left: tableRef.current.scrollLeft + 200,
        behavior: "smooth",
      });

      updateTableScrollState();
    }
  };

  const handleScrollLeft = () => {
    if (tableRef.current && canScrollLeft) {
      tableRef.current.scrollTo({
        left: tableRef.current.scrollLeft - 200,
        behavior: "smooth",
      });

      updateTableScrollState();
    }
  };

  useEffect(() => {
    if (tableRef.current) {
      updateTableScrollState();

      // Add scroll event listener to tableRef to update the scroll state
      tableRef.current.addEventListener("scroll", updateTableScrollState);
    }

    window.addEventListener("resize", updateTableScrollState);

    return () => {
      tableRef.current?.removeEventListener("scroll", updateTableScrollState);
      window.removeEventListener("resize", updateTableScrollState);
    };
  }, [tableRef.current, updateTableScrollState]);

  return (
    <div ref={wrapperRef}>
      <h3 className="font-display text-h3 leading-h3 lg:text-[32px]">
        Size Guide
      </h3>

      <div className="flex flex-col space-y-micro">
        {!!sheetContent?.sizingAdvice && (
          <ShopifyRichText content={sheetContent.sizingAdvice} />
        )}
        {!!summary && summary}
      </div>

      <div className="flex flex-col space-y-midi py-midi">
        {tableCells.length > 0 && (
          <div className="relative">
            <TableControls
              scrollRight={handleScrollRight}
              scrollLeft={handleScrollLeft}
              canScrollRight={canScrollRight}
              canScrollLeft={canScrollLeft}
            />
            <Table
              tableHeaderClassName="p-micro text-caption bg-primary-100 font-normal"
              cellClassName="p-micro text-caption"
              columns={tableHeaders}
              row={tableCells}
              orientation="horizontal"
              tableRef={tableRef}
            />
          </div>
        )}

        {!!sheetContent?.flexibleSections && (
          <div className="grid grid-cols-1 gap-micro md:grid-cols-2 md:gap-mini">
            <Sections page={{ content: sheetContent?.flexibleSections }} />
          </div>
        )}
      </div>
    </div>
  );
}

function BiomechanicalFootbedSheetContent({
  biomechanicalFootbed,
  wrapperRef,
  gender,
}: Pick<SizeAndFitProps, "biomechanicalFootbed" | "gender"> & {
  wrapperRef: RefObject<HTMLDivElement>;
}) {
  if (!biomechanicalFootbed) return null;

  const { parseContent } = useRichText();
  const content = parseContent(biomechanicalFootbed.content);
  const desktopImage =
    gender === "Mens"
      ? biomechanicalFootbed.footbedImageMenDesktop
      : biomechanicalFootbed.footbedImageWomenDesktop;
  const mobileImage =
    gender === "Mens"
      ? biomechanicalFootbed.footbedImageMenMobile
      : biomechanicalFootbed.footbedImageWomenMobile;

  const detailFootbedSections = [
    {
      title: "Metatarsal Dome",
      description:
        "Prevents discomfort by reducing pressure in the ball of the foot.",
      icon: <IconMetatarsalDome className="w-[80px]" />,
    },
    {
      title: "Cuboid Notch",
      description:
        "Helps to stabilise your foot to promote better foot function.",
      icon: <IconCuboidNotch className="w-[80px]" />,
    },
    {
      title: "Midfoot Arch Support",
      description: "Provides cushioned support for optimal comfort.",
      icon: <IconArchSupport className="w-[80px]" />,
    },

    {
      title: "Heel Cup",
      description:
        "Cushions the heel, providing shock absorption to aid in the prevention of heel pain.",
      icon: <IconHeelCup className="w-[80px]" />,
    },
  ];

  return (
    <div ref={wrapperRef} className="flex flex-col space-y-mini">
      {content}

      <div>
        {desktopImage ? (
          <SanityImage
            className={clsx(!!mobileImage ? "hidden" : "flex", "md:flex")}
            data={desktopImage.asset}
            altText={desktopImage.alt ?? ""}
            maxWidth="100vw"
          />
        ) : null}
        {mobileImage ? (
          <SanityImage
            className="md:hidden"
            data={mobileImage.asset}
            altText={mobileImage.alt ?? ""}
            maxWidth="100vw"
          />
        ) : null}
      </div>

      <div className="rte grid grid-cols-2 gap-mini pb-midi md:grid-cols-4">
        {detailFootbedSections.map((section, index) => {
          return (
            <div className="flex flex-col space-y-pico">
              {section.icon}
              <h5>
                {index + 1}. {section.title}
              </h5>
              <p className="text-hint">{section.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface TableControlsProps {
  scrollRight: () => void;
  scrollLeft: () => void;
  canScrollRight: boolean;
  canScrollLeft: boolean;
}

function TableControls({
  scrollRight,
  scrollLeft,
  canScrollRight,
  canScrollLeft,
}: TableControlsProps) {
  return (
    <div className="mb-micro flex justify-end space-x-micro">
      <button
        disabled={!canScrollLeft}
        onClick={() => scrollLeft()}
        aria-label="Previous"
        aria-disabled={!canScrollLeft}
        className={clsx(
          "focus-visible:outline-dashed focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-current",
          !canScrollLeft && "opacity-50",
        )}
      >
        <IconVintageArrow className="w-[20px] text-primary" direction="left" />
      </button>
      <button
        aria-label="Next"
        disabled={!canScrollRight}
        onClick={() => scrollRight()}
        aria-disabled={!canScrollRight}
        className={clsx(
          "focus-visible:outline-dashed focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-current",
          !canScrollRight && "opacity-50",
        )}
      >
        <IconVintageArrow className="w-[20px] text-primary" direction="right" />
      </button>
    </div>
  );
}
